import {
  Button,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  mergeClasses,
  SearchBox,
  Text,
  Tooltip,
} from "@fluentui/react-components";
import {
  Add20Filled,
  ArrowSortDownLinesRegular,
  ArrowSortUpLinesRegular,
  Info20Regular,
} from "@fluentui/react-icons";
import { deleteDiagnostic } from "apps/diagnostics/api";
import { IDiagnostic } from "apps/diagnostics/models/IDiagnostic";
import { Constants, ConstantValues } from "common/constants";
import { ActionDialog } from "components/actionDialog/ActionDialog";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store";
import {
  useSharedCardGridStyles,
  useSharedEmptyStateStyles,
  useSharedPageContainerStyles,
  useSharedPageHeaderStyles,
  useSharedPageSearchStyles,
  useSharedPillButtonStyles,
  useSharedPillTabContainerStyles,
  useSharedResponsiveSectionStyles,
  useSharedSortControlStyles,
} from "theme/sharedStyles";
import { readSortingOptions, writeSortingOptions } from "utils/generalUtils";
import { removeDiagnostic } from "../../../../redux/diagnosticsSlice";
import { showToast } from "../../../../redux/toastActions";
import { HowDiagnosticsWorkDialog } from "../howDiagnosticsWorkDialog/HowDiagnosticsWorkDialog";
import { DiagnosticCard } from "./diagnosticCard/DiagnosticCard";
import { EditDiagnostic } from "./editDialog/EditDiagnostic";
import { useDiagnosticsStyles } from "./manageDiagnostics.styles";
import {
  EMPTY_STATE_LABELS,
  CARD_VIEW_SORT_FIELD_LABELS as SORT_FIELD_LABELS,
  CardViewSortField as SortField,
  CardViewTabValue as TabValue,
} from "./ManageDiagnosticsCardView.types";

const SORTING_SCOPE = "DiagnosticsCardView";

export const ManageDiagnosticsCardView = (): JSX.Element => {
  const containerStyles = useSharedPageContainerStyles();
  const headerStyles = useSharedPageHeaderStyles();
  const sectionStyles = useSharedResponsiveSectionStyles();
  const sortStyles = useSharedSortControlStyles();
  const gridStyles = useSharedCardGridStyles();
  const pillButtonStyles = useSharedPillButtonStyles();
  const pillTabContainerStyles = useSharedPillTabContainerStyles();
  const emptyStateStyles = useSharedEmptyStateStyles();
  const pageSearchStyles = useSharedPageSearchStyles();
  const localStyles = useDiagnosticsStyles();
  const dispatch = useDispatch();

  const diagnostics: IDiagnostic[] = useSelector(
    (state: RootState) => state.diagnosticsSlice.value,
  );
  const currentUser = useSelector(
    (state: RootState) => state.currentUserSlice.value,
  );

  const [searchQuery, setSearchQuery] = useState<string>(
    ConstantValues.EMPTY_STRING,
  );
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>(
    ConstantValues.EMPTY_STRING,
  );
  const [activeTab, setActiveTab] = useState<TabValue>("my");
  const [sortField, setSortField] = useState<SortField>(
    (readSortingOptions(SORTING_SCOPE)?.field as SortField) ?? "name",
  );
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">(
    readSortingOptions(SORTING_SCOPE)?.order ?? "asc",
  );
  const [isEditPopupOpen, setIsEditPopupOpen] = useState<boolean>(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const [selectedDiagnostic, setSelectedDiagnostic] =
    useState<IDiagnostic | null>(null);
  const [showHowItWorks, setShowHowItWorks] = useState<boolean>(false);

  useEffect(() => {
    writeSortingOptions(SORTING_SCOPE, { field: sortField, order: sortOrder });
  }, [sortField, sortOrder]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const newDiagnosticButtonRef = useRef<HTMLButtonElement>(null);

  const currentUserId = currentUser?.id;
  const isDiagnosticsAdmin = !!currentUser?.roles?.includes(
    Constants.DIAGNOSTICS_ADMINS_GROUP_NAME_VALUE,
  );

  const TAB_VALUES = useMemo<TabValue[]>(
    () => (isDiagnosticsAdmin ? ["my", "shared", "admin"] : ["my", "shared"]),
    [isDiagnosticsAdmin],
  );

  useEffect(() => {
    if (!isDiagnosticsAdmin && activeTab === "admin") {
      setActiveTab("my");
    }
  }, [isDiagnosticsAdmin, activeTab]);

  const handleTabKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      const currentIndex = TAB_VALUES.indexOf(activeTab);
      let nextIndex: number | null = null;

      if (e.key === "ArrowRight") {
        nextIndex = (currentIndex + 1) % TAB_VALUES.length;
      } else if (e.key === "ArrowLeft") {
        nextIndex = (currentIndex - 1 + TAB_VALUES.length) % TAB_VALUES.length;
      }

      if (nextIndex !== null) {
        e.preventDefault();
        setActiveTab(TAB_VALUES[nextIndex]);
        tabRefs.current[nextIndex]?.focus();
      }
    },
    [activeTab, TAB_VALUES],
  );

  const filteredDiagnostics = useMemo(() => {
    return diagnostics
      .filter((diagnostic) => {
        const isOwner = diagnostic.user?.userId === currentUserId;
        const isCollaborator = diagnostic.collaborators?.some(
          (c) => c.userId === currentUserId,
        );

        if (activeTab === "my" && !isOwner) return false;
        if (activeTab === "shared" && !isCollaborator) return false;
        if (activeTab === "admin" && (isOwner || isCollaborator)) return false;

        if (debouncedSearchQuery.trim()) {
          const query = debouncedSearchQuery.toLowerCase();
          const matchesSearch =
            diagnostic.name.toLowerCase().includes(query) ||
            diagnostic.description.toLowerCase().includes(query) ||
            diagnostic.client.toLowerCase().includes(query) ||
            diagnostic.user?.displayName?.toLowerCase().includes(query) ||
            diagnostic.collaborators?.some((collaborator) =>
              collaborator.displayName?.toLowerCase().includes(query),
            );
          if (!matchesSearch) return false;
        }

        return true;
      })
      .sort((a, b) => {
        const dir = sortOrder === "asc" ? 1 : -1;
        switch (sortField) {
          case "updatedAt": {
            const timeA = a.updatedAt || a.createdAt || 0;
            const timeB = b.updatedAt || b.createdAt || 0;
            return (timeA - timeB) * dir;
          }
          case "name":
          default: {
            const nameA = a.name.toLowerCase();
            const nameB = b.name.toLowerCase();
            return nameA.localeCompare(nameB) * dir;
          }
        }
      });
  }, [
    diagnostics,
    activeTab,
    debouncedSearchQuery,
    sortField,
    sortOrder,
    currentUserId,
  ]);

  const handleCreateClick = () => {
    setIsEditPopupOpen(true);
  };

  const handleDelete = (diagnostic: IDiagnostic) => {
    setSelectedDiagnostic(diagnostic);
    setShowDeleteDialog(true);
  };

  const onDeleteConfirmed = async (): Promise<void> => {
    if (!selectedDiagnostic) return;

    try {
      await deleteDiagnostic({ id: selectedDiagnostic.id });
      dispatch(removeDiagnostic(selectedDiagnostic.id));
    } catch (error) {
      console.error("Error deleting diagnostic:", error);
      dispatch(showToast("Failed to delete diagnostic", "error"));
    } finally {
      setSelectedDiagnostic(null);
      setShowDeleteDialog(false);
    }
  };

  return (
    <div className={containerStyles.container}>
      <div className={headerStyles.header}>
        <div className={localStyles.headerLeft}>
          <h1 className={headerStyles.title}>AI Interviews - Diagnostics</h1>
          <Tooltip
            content="Learn how AI Interviews diagnostics work"
            relationship="label"
            appearance="inverted"
          >
            <button
              type="button"
              className={localStyles.infoButton}
              onClick={() => setShowHowItWorks(true)}
              aria-label="How diagnostics work"
            >
              <Info20Regular />
            </button>
          </Tooltip>
        </div>
        <Button
          ref={newDiagnosticButtonRef}
          className={headerStyles.createButton}
          onClick={handleCreateClick}
          icon={<Add20Filled />}
        >
          New Diagnostic
        </Button>
      </div>

      <div className={sectionStyles.root}>
        <SearchBox
          className={pageSearchStyles.searchInput}
          placeholder="Search diagnostics by name, client, or description..."
          aria-label="Search diagnostics"
          value={searchQuery}
          onChange={(_, data) =>
            setSearchQuery(data?.value || ConstantValues.EMPTY_STRING)
          }
        />
      </div>

      <div className={sortStyles.container}>
        <div
          className={pillTabContainerStyles.tabList}
          role="tablist"
          aria-label="Diagnostic categories"
        >
          <button
            type="button"
            ref={(el) => {
              tabRefs.current[0] = el;
            }}
            id="tab-my"
            className={mergeClasses(
              pillButtonStyles.pillButton,
              activeTab === "my" && pillButtonStyles.pillButtonActive,
            )}
            onClick={() => setActiveTab("my")}
            onKeyDown={handleTabKeyDown}
            role="tab"
            aria-selected={activeTab === "my"}
            aria-controls="tabpanel-diagnostics"
            tabIndex={activeTab === "my" ? 0 : -1}
          >
            My Diagnostics
          </button>
          <button
            type="button"
            ref={(el) => {
              tabRefs.current[1] = el;
            }}
            id="tab-shared"
            className={mergeClasses(
              pillButtonStyles.pillButton,
              activeTab === "shared" && pillButtonStyles.pillButtonActive,
            )}
            onClick={() => setActiveTab("shared")}
            onKeyDown={handleTabKeyDown}
            role="tab"
            aria-selected={activeTab === "shared"}
            aria-controls="tabpanel-diagnostics"
            tabIndex={activeTab === "shared" ? 0 : -1}
          >
            Shared With Me
          </button>
          {isDiagnosticsAdmin && (
            <button
              type="button"
              ref={(el) => {
                tabRefs.current[2] = el;
              }}
              id="tab-admin"
              className={mergeClasses(
                pillButtonStyles.pillButton,
                activeTab === "admin" && pillButtonStyles.pillButtonActive,
              )}
              onClick={() => setActiveTab("admin")}
              onKeyDown={handleTabKeyDown}
              role="tab"
              aria-selected={activeTab === "admin"}
              aria-controls="tabpanel-diagnostics"
              tabIndex={activeTab === "admin" ? 0 : -1}
            >
              Admin
            </button>
          )}
        </div>

        <div className={sortStyles.sortGroup}>
          <span id="sort-by-label-diagnostics" className={sortStyles.sortLabel}>
            Sort by:
          </span>
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <MenuButton
                appearance="outline"
                className={sortStyles.sortMenuButton}
                aria-labelledby="sort-by-label-diagnostics"
              >
                {SORT_FIELD_LABELS[sortField]}
              </MenuButton>
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem onClick={() => setSortField("name")}>Name</MenuItem>
                <MenuItem onClick={() => setSortField("updatedAt")}>
                  Last Updated
                </MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
          <Tooltip
            content={
              sortOrder === "asc"
                ? "Currently ascending, sort descending"
                : "Currently descending, sort ascending"
            }
            relationship="label"
            appearance="inverted"
          >
            <Button
              appearance="subtle"
              icon={
                sortOrder === "asc" ? (
                  <ArrowSortDownLinesRegular />
                ) : (
                  <ArrowSortUpLinesRegular />
                )
              }
              className={sortStyles.sortDirectionButton}
              onClick={() =>
                setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"))
              }
              aria-label={
                sortOrder === "asc"
                  ? "Currently ascending, sort descending"
                  : "Currently descending, sort ascending"
              }
            />
          </Tooltip>
        </div>
      </div>

      <div
        id="tabpanel-diagnostics"
        role="tabpanel"
        aria-labelledby={`tab-${activeTab}`}
        className={gridStyles.gridScroll}
      >
        <div className={gridStyles.gridContainer}>
          {filteredDiagnostics.length > 0 ? (
            <div className={gridStyles.grid}>
              {filteredDiagnostics.map((d: IDiagnostic) => (
                <DiagnosticCard
                  key={d.id}
                  diagnostic={d}
                  onDelete={handleDelete}
                  isOwner={d.user?.userId === currentUserId}
                  isDiagnosticsAdmin={isDiagnosticsAdmin}
                />
              ))}
            </div>
          ) : (
            <div className={emptyStateStyles.container}>
              <Text size={300} weight="regular">
                {debouncedSearchQuery.trim()
                  ? EMPTY_STATE_LABELS.NO_SEARCH_RESULTS
                  : EMPTY_STATE_LABELS.NO_DIAGNOSTICS}
              </Text>
            </div>
          )}
        </div>
      </div>

      {showDeleteDialog && (
        <ActionDialog
          isVisible={showDeleteDialog}
          title="Delete Diagnostic"
          onConfirmation={onDeleteConfirmed}
          onDismiss={() => {
            setShowDeleteDialog(false);
            newDiagnosticButtonRef.current?.focus();
          }}
          confirmationMessage="Delete"
          dismissMessage="Cancel"
          message={`Are you sure you want to delete "${selectedDiagnostic?.name}"? This action cannot be undone.`}
        />
      )}

      {showHowItWorks && (
        <HowDiagnosticsWorkDialog
          open={showHowItWorks}
          onClose={() => setShowHowItWorks(false)}
        />
      )}

      {isEditPopupOpen && (
        <EditDiagnostic
          isOpen={isEditPopupOpen}
          isDiagnosticsAdmin={isDiagnosticsAdmin}
          onDismiss={() => {
            setIsEditPopupOpen(false);
            newDiagnosticButtonRef.current?.focus();
          }}
        />
      )}
    </div>
  );
};
